/**
 * Helper utilities for generating printable cooking plans
 */

/**
 * Format time in seconds to readable format
 * @param {number} seconds - Time in seconds
 * @param {boolean} includeSeconds - Whether to include seconds in output
 * @returns {string} Formatted time string (e.g., "1h 30m" or "45m 30s")
 */
export const formatTime = (seconds, includeSeconds = false) => {
    if (!seconds || seconds === 0) return '0m';
    
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || hours === 0) parts.push(`${mins}m`);
    if (includeSeconds && secs > 0) parts.push(`${secs}s`);
    
    return parts.join(' ') || '0m';
};

/**
 * Format time as HH:MM:SS or MM:SS
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string (e.g., "01:30:00" or "45:30")
 */
export const formatTimeClock = (seconds) => {
    if (!seconds || seconds === 0) return '00:00';
    
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Sort segments chronologically by start time
 * @param {Array} segments - Array of segment objects
 * @returns {Array} Sorted segments
 */
export const sortSegmentsChronologically = (segments) => {
    return [...segments].sort((a, b) => {
        if (a.startSec !== b.startSec) {
            return a.startSec - b.startSec;
        }
        // If same start time, sort by recipe, then step
        if (a.recipeName !== b.recipeName) {
            return a.recipeName.localeCompare(b.recipeName);
        }
        return (a.stepIndex || 0) - (b.stepIndex || 0);
    });
};

/**
 * Group segments by recipe for better organization
 * @param {Array} segments - Array of segment objects
 * @returns {Object} Segments grouped by recipe name
 */
export const groupSegmentsByRecipe = (segments) => {
    const grouped = {};
    
    segments.forEach(segment => {
        const recipeName = segment.recipeName;
        if (!grouped[recipeName]) {
            grouped[recipeName] = [];
        }
        grouped[recipeName].push(segment);
    });
    
    // Sort segments within each recipe by start time
    Object.keys(grouped).forEach(recipeName => {
        grouped[recipeName].sort((a, b) => a.startSec - b.startSec);
    });
    
    return grouped;
};

/**
 * Generate chronological instructions from segments
 * @param {Array} segments - Array of segment objects
 * @returns {Array} Array of instruction objects
 */
export const generateInstructions = (segments) => {
    const sorted = sortSegmentsChronologically(segments);
    
    return sorted.map(segment => ({
        time: segment.startSec,
        endTime: segment.endSec,
        recipeName: segment.recipeName,
        recipeId: segment.recipeId,
        stepIndex: segment.stepIndex || 0,
        step: `Step ${(segment.stepIndex || 0) + 1}`,
        action: segment.stepTitle || segment.label || 'No description',
        type: segment.type || 'active',
        duration: segment.durationSec || 0,
        label: segment.label
    }));
};

/**
 * Extract and organize ingredients from recipes
 * @param {Array} recipes - Array of recipe objects
 * @returns {Object} Ingredients grouped by recipe name, sorted alphabetically
 */
export const extractIngredients = (recipes) => {
    const ingredientsByRecipe = {};
    
    recipes.forEach(recipe => {
        const recipeName = recipe.name || recipe.title;
        if (recipeName) {
            ingredientsByRecipe[recipeName] = {
                ingredients: recipe.ingredients || [],
                category: recipe.category || null,
                id: recipe.id || recipe.recipeId
            };
        }
    });
    
    // Sort recipe names alphabetically
    const sorted = {};
    Object.keys(ingredientsByRecipe)
        .sort()
        .forEach(key => {
            sorted[key] = ingredientsByRecipe[key];
        });
    
    return sorted;
};

/**
 * Calculate summary statistics from schedule
 * @param {Object} scheduledPlan - The scheduled plan object
 * @param {Array} recipes - Array of recipe objects
 * @returns {Object} Summary statistics
 */
export const calculateSummary = (scheduledPlan, recipes) => {
    const { segments = [], makespanSec = 0, activeTotalSec = 0, inactiveTotalSec = 0 } = scheduledPlan;
    
    // Count steps per recipe
    const stepsByRecipe = {};
    segments.forEach(seg => {
        const recipeName = seg.recipeName;
        if (!stepsByRecipe[recipeName]) {
            stepsByRecipe[recipeName] = new Set();
        }
        stepsByRecipe[recipeName].add(seg.stepIndex || 0);
    });
    
    const recipeStats = Object.keys(stepsByRecipe).map(recipeName => ({
        name: recipeName,
        stepCount: stepsByRecipe[recipeName].size,
        activeTime: segments
            .filter(s => s.recipeName === recipeName && s.type === 'active')
            .reduce((sum, s) => sum + (s.durationSec || 0), 0),
        inactiveTime: segments
            .filter(s => s.recipeName === recipeName && s.type === 'inactive')
            .reduce((sum, s) => sum + (s.durationSec || 0), 0)
    }));
    
    return {
        totalTime: makespanSec,
        activeTime: activeTotalSec,
        inactiveTime: inactiveTotalSec,
        recipeCount: recipes.length,
        totalSteps: segments.reduce((sum, seg) => {
            const stepKey = `${seg.recipeId}-${seg.stepIndex || 0}`;
            return sum + (seg.stepIndex !== undefined ? 1 : 0);
        }, 0),
        recipeStats
    };
};

